import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Box,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  ClickAwayListener,
  Popper,
  Paper,
  Typography,
  MenuList,
  TextField,
} from "@mui/material";

interface Option {
  label: string;
  value: string;
  id: string;
}

interface MuiMultiSelectProps {
  label?: string;
  placeholder?: string;
  value: string[];
  options: Option[];
  onChange: (value: string[], ids: string[]) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  enableSelectAll?: boolean;
  inputValue?: string;
  onSearchInputChange?: (value: string) => void;
  isRequired?: boolean;
  errorText?: string;
  isInvalid?: boolean;
}

const MuiMultiSelect: React.FC<MuiMultiSelectProps> = ({
  label,
  placeholder = "Select...",
  value,
  options,
  onChange,
  disabled = false,
  fullWidth = true,
  size = "small",
  enableSelectAll,
  inputValue,
  onSearchInputChange,
  isRequired,
  errorText,
  isInvalid,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string[]>(value);
  const [searchTerm, setSearchTerm] = useState(inputValue || "");

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredValues = filteredOptions.map((opt) => opt.value);
  const isAllFilteredSelected = filteredValues.every((val) =>
    tempValue.includes(val)
  );

  const handleItemChange = (val: string) => {
    if (val === "__all__") {
      if (isAllFilteredSelected) {
        setTempValue((prev) => prev.filter((v) => !filteredValues.includes(v)));
      } else {
        setTempValue((prev) =>
          Array.from(new Set([...prev, ...filteredValues]))
        );
      }
    } else {
      setTempValue((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
      );
    }
  };

  const handleApply = () => {
    const selectedIds = tempValue
      .map((val) => options.find((opt) => opt.value === val)?.id)
      .filter(Boolean) as string[];
    onChange(tempValue, selectedIds);
    setOpen(false);
  };

  const handleDeleteChip = (val: string) => {
    const updated = value.filter((v) => v !== val);
    const updatedIds = updated
      .map((v) => options.find((opt) => opt.value === v)?.id)
      .filter(Boolean) as string[];
    onChange(updated, updatedIds);
    setTempValue(updated);
  };

  const renderChips = () => {
    if (!value || value.length === 0 || value[0] === "")
      return (
        <Typography sx={{ color: "#888" }}>
          {placeholder}
          {isRequired && <span style={{ color: "red" }}> *</span>}
        </Typography>
      );

    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          flexWrap: "nowrap",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {value.map((val) => {
          const label = options.find((opt) => opt.value === val)?.label || val;
          return (
            <Chip
              key={val}
              label={label}
              onDelete={() => handleDeleteChip(val)}
            />
          );
        })}
      </Box>
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    onSearchInputChange?.(e.target.value);
  };

  return (
    <>
      <FormControl fullWidth={fullWidth} size={size}>
        {value?.length > 0 && label && (
          <InputLabel shrink>
            {label}
            {isRequired && <span style={{ color: "red" }}> *</span>}
          </InputLabel>
        )}

        <Box
          ref={anchorRef}
          onClick={() => !disabled && setOpen(!open)}
          sx={{
            border: `1px solid ${
              isInvalid && value.length === 0 ? "#d32f2f" : "#ccc"
            }`,
            borderRadius: "8px",
            minHeight: 52,
            padding: "6px 10px",
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            backgroundColor: disabled ? "#f5f5f5" : "white",
          }}
        >
          {renderChips()}
        </Box>

        {isInvalid && value.length === 0 && errorText && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: "4px", ml: "8px" }}
          >
            {errorText}
          </Typography>
        )}
      </FormControl>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper sx={{ width: 365 }}>
            {/* Search Input */}
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Box>

            {/* Scrollable MenuList */}
            <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
              <MenuList dense key={searchTerm}>
                {enableSelectAll && filteredOptions.length > 0 && (
                  <MenuItem onClick={() => handleItemChange("__all__")}>
                    <Checkbox checked={isAllFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                )}

                {filteredOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleItemChange(option.value)}
                  >
                    <Checkbox checked={tempValue.includes(option.value)} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </MenuList>
            </Box>

            {/* Fixed OK Button */}
            <Box
              sx={{
                p: 1,
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#fff",
                position: "sticky",
                bottom: 0,
                zIndex: 1,
              }}
            >
              <Button variant="contained" size="small" onClick={handleApply}>
                OK
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default MuiMultiSelect;
